"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, CircleCheck as CheckCircle2, Droplets, PhoneCall, ShieldCheck, Wrench, Wind } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const processSteps = [{
  stepNumber: "01",
  phase: "Phase 1: Rapid Extraction",
  title: "Emergency Water Extraction",
  description: "Our local crew deploys truck-mounted vacuum units and submersible pumps to remove standing water, preventing secondary seepage into framing and subfloors.",
  spec: "Commercial truck pumps & deep-extraction extractors",
  timeframe: "Immediate arrival"
}, {
  stepNumber: "02",
  phase: "Phase 2: Climate Stabilization",
  title: "Structural Drying & Dehumidification",
  description: "We place industrial desiccant dehumidifiers and high-velocity air movers to pull bound moisture from drywall, floorboards, and wall cavities until dry standards are met.",
  spec: "Desiccant dehumidification & daily moisture logs",
  timeframe: "Day 1 – Day 4"
}, {
  stepNumber: "03",
  phase: "Phase 3: Source Containment",
  title: "Plumbing Repair & Basement Waterproofing",
  description: "Technicians isolate the failure source—whether a burst water main, failed sump pump, or cracked foundation seam—and carry out permanent plumbing fixes and barrier seals.",
  spec: "Pipe pressure diagnostics & structural sealants",
  timeframe: "Active mitigation"
}, {
  stepNumber: "04",
  phase: "Phase 4: Complete Recovery",
  title: "Full Restoration & Repair",
  description: "We apply EPA-registered antimicrobial treatments, rebuild damaged structural framing, hang new drywall, and complete a final infrared moisture inspection before handover.",
  spec: "Antimicrobial seal & formal insurance clearance",
  timeframe: "Final sign-off"
}];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const getStepIcon = (index: number) => {
    switch (index) {
      case 0:
        return <Droplets className="size-5 text-primary" />;
      case 1:
        return <Wind className="size-5 text-primary" />;
      case 2:
        return <Wrench className="size-5 text-primary" />;
      case 3:
        return <ShieldCheck className="size-5 text-primary" />;
      default:
        return <Activity className="size-5 text-primary" />;
    }
  };
  return <section id="our-process" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Operational Overline */}
        <div className="flex items-center gap-3 mb-6">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-primary/10 border border-primary/20 text-primary text-xs font-bold uppercase tracking-wider">
            <span className="size-1.5 rounded-full bg-primary animate-pulse" />
            Standard Mitigation Workflow
          </span>
          <div className="hidden sm:block h-px flex-1 bg-border" />
          <span className="hidden sm:inline-block font-mono text-xs text-muted-foreground uppercase tracking-wider">
            MD • DC • Northern Virginia
          </span>
        </div>

        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            How we dry and restore your property
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Four clear phases from immediate water extraction to final structural sign-off.
          </p>
          <p className="text-sm sm:text-base text-muted-foreground/90 mt-2 leading-relaxed">
            When floodwaters or burst pipes penetrate your building, speed protects your investment. Our local technicians work directly on scene without third-party dispatchers, measuring moisture readings daily and coordinating reports with your insurance company.
          </p>
        </div>

        {/* 4-Column Process Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {processSteps.map((step, i) => <motion.div key={step.stepNumber} data-index={i} variants={itemVariants} className="group relative flex flex-col justify-between rounded-lg border border-border bg-muted/50 p-6 transition-[background-color,border-color,box-shadow] duration-200 hover:bg-card hover:border-primary/50 hover:shadow-md">
              <div>
                {/* Step Top Bar: Index & Icon */}
                <div className="flex items-center justify-between mb-5">
                  <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-background border border-border text-foreground">
                    {step.stepNumber}
                  </span>
                  <div className="p-2 rounded-md bg-primary/10 border border-primary/20 text-primary flex items-center justify-center">
                    {getStepIcon(i)}
                  </div>
                </div>

                {/* Subtitle / Phase Tag */}
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  {step.phase}
                </p>

                {/* Step Title */}
                <h3 className="text-lg font-bold text-foreground mb-3 leading-snug">
                  {step.title}
                </h3>

                {/* Step Description */}
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                  {step.description}
                </p>
              </div>

              {/* Technical Spec Footer Bar */}
              <div className="pt-4 border-t border-border/80">
                <div className="flex items-center gap-1.5 text-xs text-foreground font-medium mb-1">
                  <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                  <span className="truncate">{step.spec}</span>
                </div>
                <div className="flex justify-between items-center text-[11px] text-muted-foreground font-mono">
                  <span>Target duration:</span>
                  <span className="font-semibold text-foreground">{step.timeframe}</span>
                </div>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Operational Dispatch Box */}
        <div className="mt-12 md:mt-16 rounded-lg border border-border bg-card p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-xs">
          <div className="space-y-1 max-w-xl">
            <div className="flex items-center gap-2">
              <span className="size-2 rounded-full bg-primary" />
              <p className="text-xs font-bold uppercase tracking-wider text-primary">
                Direct Dispatch • No Call Centers
              </p>
            </div>
            <h4 className="text-base sm:text-lg font-bold text-foreground">
              Need water extracted right now?
            </h4>
            <p className="text-sm text-muted-foreground">
              Our regional response vehicles are stocked with desiccant drying units and high-capacity water pumps ready for deployment across MD, DC, and Northern Virginia.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-6 h-11">
              <a href="tel:855-435-6631" className="inline-flex items-center justify-center gap-2">
                <PhoneCall className="size-4" />
                <span>Call 855-435-6631</span>
              </a>
            </Button>

            <Button asChild variant="outline" className="border-input hover:bg-muted font-semibold px-5 h-11 text-foreground">
              <Link to="/ContactRequestServicePage">
                Request Service
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}